import { Injectable } from '@angular/core';
import { filterAllTruthyInTuple, subjectFromObs } from 'core/rx-js';
import { BehaviorSubject, Subject, combineLatest, debounceTime, distinctUntilChanged, filter, fromEvent, interval, map, startWith, switchMap, takeUntil } from 'rxjs';
import { PdfViewerService } from '../../pdf-viewer.service';

@Injectable()
export class PdfThumbnailViewportsContainerService {


  private readonly _destroy = new Subject<void>()
  readonly scrollTopSubject = new BehaviorSubject<number>(0);

  readonly pageIdxSubject = this._pdfViewerService.pageIdxSubject;
  readonly thumbnailScrollerSubject = new BehaviorSubject<HTMLDivElement>(null);

  readonly width = this._pdfViewerService.THUMBNAIL_WIDTH;
  readonly pdfPageInfosSubject = this._pdfViewerService.pdfPageInfosSubject;
  readonly pageLoadFnSubject = this._pdfViewerService.pageLoadFnSubject;

  readonly scaledPdfPageInfosSubject = subjectFromObs({
    observable: this._pdfViewerService.pdfDocumentInputsSubject.pipe(
      filter(Boolean),
      map((pdfDocumentInputs) => this._pdfViewerService.getPageInfos({ pdfDocumentInputs, scaleToWidth: this.width })),
    ),
    until: this._destroy,
    initialValue: null
  })


  readonly visiblePageRangeSubject = subjectFromObs({
    observable: combineLatest([this.thumbnailScrollerSubject, this.scaledPdfPageInfosSubject, this.scrollTopSubject])
      .pipe(
        filter(([thumbnailScroller, scaledPdfPageInfos]) => Boolean(thumbnailScroller && scaledPdfPageInfos)),
        map(([thumbnailScroller, scaledPdfPageInfos, scrollTop]) => {

          const min = scrollTop;
          const max = scrollTop + thumbnailScroller.clientHeight;

          let firstRenderedPageIdx;
          let lastRenderedPageIdx;
          for (const pageInfo of scaledPdfPageInfos) {

            if (pageInfo.scrollTop + pageInfo.dimensions.height > min && firstRenderedPageIdx === undefined) {
              firstRenderedPageIdx = pageInfo.pageIdx;
            }

            if (pageInfo.scrollTop + pageInfo.dimensions.height > max && lastRenderedPageIdx === undefined) {
              lastRenderedPageIdx = pageInfo.pageIdx;
              return {
                lastRenderedPageIdx,
                firstRenderedPageIdx
              }
            }
          }

          return {
            firstRenderedPageIdx,
            lastRenderedPageIdx: scaledPdfPageInfos.length
          }
        }),
        distinctUntilChanged((prev, next) => prev.firstRenderedPageIdx === next.firstRenderedPageIdx && prev.lastRenderedPageIdx === next.lastRenderedPageIdx),
      ),
    initialValue: null
  })



  readonly renderedBatchesSubject = this._pdfViewerService.renderedBatchesSubject;
  readonly maxPageRangeObs = this._pdfViewerService.maxPageRangeObs;

  readonly renderedRangeObs = combineLatest([this.visiblePageRangeSubject, this.maxPageRangeObs.pipe(startWith(null))])
    .pipe(
      filterAllTruthyInTuple(),
      map(([{ firstRenderedPageIdx, lastRenderedPageIdx }, { max, min }]) => ({
        start: Math.max(firstRenderedPageIdx - 4, min),
        end: Math.min(max, lastRenderedPageIdx + 4)
      }))
    )

  readonly PAGE_BATCH_SIZE = this._pdfViewerService.PAGE_BATCH_SIZE;


  readonly renederedBatchIdxsObs = this.renderedRangeObs.pipe(
    filter(Boolean),
    map(({ start, end }) => {

      const startBatch = Math.floor(start / this.PAGE_BATCH_SIZE);
      const endBatch = Math.floor(end / this.PAGE_BATCH_SIZE);

      const renderedBatchArray: number[] = []

      for (let idx = startBatch; idx <= endBatch; idx++) {
        renderedBatchArray.push(idx)
      }

      return renderedBatchArray;
    }),
  )




  readonly thumbnailScrollerHeightSubject = subjectFromObs({
    observable: this.thumbnailScrollerSubject.pipe(
      filter(Boolean),
      switchMap((scroller) =>
        interval(1000).pipe(map(() => scroller.clientHeight))
      ),
      distinctUntilChanged()
    ),
    initialValue: 0,
    until: this._destroy
  })
  readonly pageBase64pngsSubject = this._pdfViewerService.pageBase64pngsSubject;
  constructor(
    private readonly _pdfViewerService: PdfViewerService
  ) {




    this.thumbnailScrollerSubject.pipe(
      takeUntil(this._destroy),
      filter(Boolean),
      switchMap((scroller) => fromEvent(scroller, 'scroll').pipe(map((ev) => ev.target as HTMLDivElement))),
      map((scroller) => scroller.scrollTop),
      distinctUntilChanged(),
      debounceTime(20)
    )
      .subscribe((scrollTop) => {

        this.scrollTopSubject.next(scrollTop);
      })

    combineLatest([this.scaledPdfPageInfosSubject, this.pageIdxSubject, this.thumbnailScrollerHeightSubject])
      .pipe(
        filterAllTruthyInTuple(),
        map(([pdfPageInfos, pageIdx, thumbnailScrollerHeight]) => ({ pdfPageInfo: pdfPageInfos.find((pageInfo) => pageIdx === pageInfo.pageIdx), thumbnailScrollerHeight })),
        filter(({ pdfPageInfo }) => Boolean(pdfPageInfo)),
        map(({ pdfPageInfo, thumbnailScrollerHeight }) => pdfPageInfo.scrollTop - Math.max(thumbnailScrollerHeight - pdfPageInfo.dimensions.height, 0) / 2),
        takeUntil(this._destroy)
      )
      .subscribe((scrollTop) => {

        this.scrollTopSubject.next(scrollTop)
      })


    combineLatest([this.renederedBatchIdxsObs, this.pageLoadFnSubject, this._pdfViewerService.pdfDocumentInputsSubject])
      .pipe(this._pdfViewerService.loadPages(), takeUntil(this._destroy))
      .subscribe(({ pageBase64pngs, prevRenderedBatches }) => {
        this.pageBase64pngsSubject.next(pageBase64pngs);
        this.renderedBatchesSubject.next(prevRenderedBatches)
      });


  }

  //   combineLatest([
  //     this.virtualScrollableSubject,
  //     this.pdfDocumentsExtendedSubject,
  //     this.selectedPageIdxObs,
  //     this.sidebarOpenedSubject.pipe(debounceTime(200))
  //   ])
  //     .pipe(
  //       takeUntil(this._destroy),
  //       filterAllTruthyInTuple()
  //     )
  //     .subscribe(([
  //       virtualScrollable,
  //       pdfDocuments,
  //       selectedPageIdx,
  //     ]) => {

  //       const { pageIdx: selectedPage, source } = this.selectedPageSubject.value;
  //       const shouldScroll = source !== 'thumbnailSelect'

  //       const docId = this._pdfViewerService.getDocumentIdFromPage(selectedPage);

  //       if (!selectedPage || !shouldScroll || !docId) {
  //         return;
  //       }

  //       const pdfDocument = pdfDocuments.find((doc) => doc.id.toString() == docId);

  //       if (!pdfDocument) {
  //         return;
  //       }

  //       const thisDocumentStartPageIdx = pdfDocument.pagesOffset + 1;
  //       const thisDocumentEndPageIdx = thisDocumentStartPageIdx + pdfDocument.pages?.length - 1;
  //       const pageInThisDocument = selectedPage >= thisDocumentStartPageIdx && selectedPage <= thisDocumentEndPageIdx

  //       if (!pageInThisDocument) {
  //         return;
  //       }

  //       const TITLE_OFFSET = 100;
  //       const thisDocumentPage = selectedPage - pdfDocument.pagesOffset - 1;
  //       const pageStartOffset = pdfDocument.viewportHeight * thisDocumentPage + TITLE_OFFSET * (pdfDocument.ord + 1) + pdfDocument.viewportHeightOffset;

  //       virtualScrollable.scrollTo({ top: Math.max(pageStartOffset - 919 / 3, 0) });

  //     })
  // }


  // ngOnDestroy() {
  //   this._destroy.next()
  //   this._destroy.complete()

  //   this.virtualScrollableSubject.complete();
  //   this.pdfThumbnailViewportHeightsSubject.complete();
  // }
}
